import { NAV_COMMAND_ID_BY_KEY } from './useGoToStarter'
import { ORG_SETTINGS_ID_BY_KEY, PROJECT_SETTINGS_ID_BY_KEY } from './useSettingsStarter'

/** `nav-project-dashboards` → `dashboards`; `nav-org-org-inventory` → `org-inventory`. */
function stripSectionPrefix(commandId: string): string {
	return commandId.replace(/^nav-(project|org)-/, '')
}

/**
 * Nav item id (the same `NavItem.id` the sidebar already renders from) to its sequence binding, e.g.
 * `dashboards` → `G then B`. Derived from the starters' own key maps rather than duplicated, so a sidebar
 * hint can never name a sequence the engine doesn't actually run.
 */
const NAV_SEQUENCE_HINT_BY_ITEM_ID: Record<string, string> = {
	...Object.fromEntries(
		Object.entries(NAV_COMMAND_ID_BY_KEY)
			// `P` is an alias for the same destination as `D` ("current project's home") — `D` is the one
			// shown, so a later duplicate must not overwrite the earlier, canonical key's entry.
			.reverse()
			.map(([key, commandId]) => [stripSectionPrefix(commandId), `G then ${key.toUpperCase()}`]),
	),
	...Object.fromEntries(
		Object.entries(PROJECT_SETTINGS_ID_BY_KEY).map(([key, itemId]) => [itemId, `S then ${key.toUpperCase()}`]),
	),
	...Object.fromEntries(
		Object.entries(ORG_SETTINGS_ID_BY_KEY).map(([key, itemId]) => [itemId, `S then ${key.toUpperCase()}`]),
	),
}

export interface NavSequenceContinuation {
	/** Full chord for display before it's armed, e.g. "G then D". */
	hint: string
	/** Lowercase starter key, e.g. "g". */
	starter: string
	/** Lowercase second key, e.g. "d". */
	key: string
}

/** The sequence that opens this nav item, if any — `undefined` for a page no sequence reaches. */
export function getNavSequenceContinuation(itemId: string): NavSequenceContinuation | undefined {
	const hint = NAV_SEQUENCE_HINT_BY_ITEM_ID[itemId]
	if (!hint) {
		return undefined
	}
	const [starter, key] = hint.split(' then ')
	return { hint, starter: starter.toLowerCase(), key: key.toLowerCase() }
}
